/**
 * PDF-Export fuer Songs, Alben, Setlists und ganze Projekte.
 *
 * Kein PDF-Paket: die Seiten werden als HTML gebaut und ueber das
 * Druckfenster des Systems als PDF gesichert (iPad: Teilen → „In Dateien
 * sichern“, Mac/Windows: „Als PDF sichern“). Dadurch bleibt Text echter,
 * durchsuchbarer Text, Akkorde sitzen exakt wie im Lead Sheet, und die App
 * bekommt keine Abhaengigkeit dazu.
 *
 * Das Muster ist immer dasselbe, damit es schnell geht und nichts kollidiert:
 *   - Titelseite mit Inhaltsverzeichnis (nur bei mehreren Songs)
 *   - jeder Song beginnt auf einer neuen Seite
 *   - Kopf: Titel, Interpret, Tonart · Tempo · Takt
 *   - Lead Sheet und/oder Songtext (bei „Beides“ und langem Sheet der Text
 *     auf eigener Seite); Strophen, Zeilen und Fotos werden nie
 *     ueber eine Seite getrennt, Ueberschriften nie allein ans Seitenende
 *   - langer Songtext mit kurzen Zeilen laeuft zweispaltig
 *
 * Gebaut wird ausserhalb des sichtbaren Bereichs in Druckbreite, damit die
 * Akkord-Takte (fitGridBars) schon vor dem Drucken richtig vermessen sind.
 */
import type { Song } from '../../types.ts';
import { h } from '../../ui/dom.ts';
import { parseChordPro } from '../../chordpro/parse.ts';
import { fitGridBars, renderSheet } from '../../chordpro/render.ts';
import { hasOwnLyrics, lyricsOf } from '../prompter-text.ts';
import type { PrompterLine } from '../prompter-text.ts';

/** Was vom Song ins PDF kommt. */
export type PdfContent = 'sheet' | 'lyrics' | 'both';

export interface PdfOptions {
  content: PdfContent;
  notes: boolean;
  photos: boolean;
}

export interface PdfDocument {
  /** z. B. "Album", "Setlist", "Projekt" – nur bei mehreren Songs sichtbar. */
  kind: string;
  title: string;
  subtitle?: string;
  /** Cover-Bild (Data-URL) fuer die Titelseite. */
  artwork?: string;
  /** Notizen zur Sammlung (z. B. der Setlist), stehen auf der Titelseite. */
  notes?: string;
  songs: Song[];
}

/** Ab so vielen Textzeilen und so kurzen Zeilen laeuft der Songtext zweispaltig. */
const TWO_COLUMN_LINES = 34;
const TWO_COLUMN_CHARS = 46;
/** Ab so vielen Lead-Sheet-Zeilen beginnt der Songtext bei „Beides“ auf neuer Seite. */
const NEW_PAGE_LINES = 22;

/* ------------------------------------------------------------- Kopf */

/** Aeltere oder importierte Songs haben nicht jedes Feld – nie daran scheitern. */
const text = (value: string | undefined | null): string => (typeof value === 'string' ? value.trim() : '');

function metaLine(song: Song): HTMLElement | null {
  const items: [string, string][] = [];
  if (text(song.key)) items.push(['Tonart', text(song.key)]);
  if (song.tempo) items.push(['Tempo', `${song.tempo} BPM`]);
  if (text(song.timeSignature)) items.push(['Takt', text(song.timeSignature)]);
  if (items.length === 0) return null;
  return h(
    'dl',
    { class: 'pdf-meta' },
    ...items.map(([label, value]) => h('div', {}, h('dt', { text: label }), h('dd', { text: value }))),
  );
}

function songHead(song: Song, running: string | null): HTMLElement {
  return h(
    'header',
    { class: 'pdf-song__head' },
    running ? h('p', { class: 'pdf-running', text: running }) : null,
    h('h1', { class: 'pdf-song__title', text: song.title || 'Ohne Titel' }),
    text(song.artist) ? h('p', { class: 'pdf-song__artist', text: text(song.artist) }) : null,
    metaLine(song),
  );
}

/* ------------------------------------------------------- Songtext */

/** Zeilen in Strophen gliedern: jede Strophe bleibt beim Drucken zusammen. */
function stanzas(lines: PrompterLine[]): { label: string | null; lines: PrompterLine[] }[] {
  const out: { label: string | null; lines: PrompterLine[] }[] = [];
  let current: { label: string | null; lines: PrompterLine[] } | null = null;
  for (const line of lines) {
    if (line.kind === 'gap') {
      current = null;
      continue;
    }
    if (line.kind === 'label') {
      current = { label: line.text, lines: [] };
      out.push(current);
      continue;
    }
    if (!current) {
      current = { label: null, lines: [] };
      out.push(current);
    }
    current.lines.push(line);
  }
  return out.filter((s) => s.lines.length > 0 || s.label);
}

function lyricsBlock(song: Song): HTMLElement {
  const lines = lyricsOf(song);
  const textLines = lines.filter((l) => l.kind === 'text');
  if (textLines.length === 0) return h('p', { class: 'pdf-empty', text: 'Kein Songtext hinterlegt.' });

  const longest = Math.max(...textLines.map((l) => l.text.length));
  const twoColumns = textLines.length >= TWO_COLUMN_LINES && longest <= TWO_COLUMN_CHARS;

  return h(
    'div',
    { class: 'pdf-lyrics' + (twoColumns ? ' pdf-lyrics--columns' : '') },
    ...stanzas(lines).map((stanza) =>
      h(
        'div',
        { class: 'pdf-stanza' },
        stanza.label ? h('p', { class: 'pdf-stanza__label', text: stanza.label }) : null,
        ...stanza.lines.map((line) => h('p', { class: 'pdf-stanza__line', text: line.text })),
      ),
    ),
  );
}

/* ---------------------------------------------------- Lead Sheet */

function sheetBlock(song: Song): HTMLElement | null {
  const parsed = parseChordPro(song.body ?? '');
  const hasContent = parsed.lines.some((l) => l.kind !== 'spacer');
  if (!hasContent) return null;
  const body = h('div', { class: 'sheet-body pdf-sheet__body' });
  renderSheet(parsed, body);
  return h('div', { class: 'sheet pdf-sheet' }, body);
}

function photosBlock(song: Song): HTMLElement | null {
  const photos = song.photos ?? [];
  if (photos.length === 0) return null;
  return h(
    'div',
    { class: 'pdf-photos' },
    ...photos.map((photo, i) => h('img', { class: 'pdf-photo', attrs: { src: photo.src, alt: `Foto ${i + 1}` } })),
  );
}

function notesBlock(value: string, title = 'Notizen'): HTMLElement | null {
  if (!value.trim()) return null;
  return h('aside', { class: 'pdf-notes' }, h('p', { class: 'pdf-notes__title', text: title }), h('p', { class: 'pdf-notes__text', text: value.trim() }));
}

function sectionTitle(text: string): HTMLElement {
  return h('h2', { class: 'pdf-section', text });
}

/* ------------------------------------------------------------ Song */

function songPage(song: Song, opts: PdfOptions, running: string | null): HTMLElement {
  const page = h('article', { class: 'pdf-song' }, songHead(song, running));
  const sheet = opts.content !== 'lyrics' ? sheetBlock(song) : null;
  const photos = opts.photos ? photosBlock(song) : null;

  if (opts.content === 'sheet') {
    if (sheet) page.append(sheet);
    else if (!photos) page.append(h('p', { class: 'pdf-empty', text: 'Noch kein Lead Sheet.' }));
  } else if (opts.content === 'lyrics') {
    page.append(lyricsBlock(song));
  } else {
    if (sheet) page.append(sectionTitle('Lead Sheet'), sheet);
    // Beides: ohne Text kein leerer Abschnitt. Bei langem Lead Sheet beginnt
    // der Songtext auf einer eigenen Seite, bei kurzem direkt darunter.
    if (lyricsOf(song).some((l) => l.kind === 'text') || !sheet) {
      const long = parseChordPro(song.body ?? '').lines.length > NEW_PAGE_LINES;
      page.append(
        h(
          'section',
          { class: sheet ? (long ? 'pdf-part pdf-part--new-page' : 'pdf-part') : 'pdf-part pdf-part--first' },
          sectionTitle(hasOwnLyrics(song) ? 'Songtext' : 'Songtext (aus dem Lead Sheet)'),
          lyricsBlock(song),
        ),
      );
    }
  }
  if (photos) page.append(photos);
  if (opts.notes) {
    const notes = notesBlock(text(song.notes));
    if (notes) page.append(notes);
  }
  return page;
}

/* ------------------------------------------------------ Titelseite */

function dateText(): string {
  return new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function coverPage(doc: PdfDocument, opts: PdfOptions): HTMLElement {
  const count = doc.songs.length;
  const toc = h(
    'ol',
    { class: 'pdf-toc' },
    ...doc.songs.map((song, i) =>
      h(
        'li',
        { class: 'pdf-toc__row' },
        h('span', { class: 'pdf-toc__nr', text: String(i + 1).padStart(2, '0') }),
        h('span', { class: 'pdf-toc__title', text: song.title || 'Ohne Titel' }),
        h('span', { class: 'pdf-toc__artist', text: text(song.artist) }),
        h('span', { class: 'pdf-toc__key', text: text(song.key) }),
      ),
    ),
  );
  return h(
    'section',
    { class: 'pdf-cover' },
    h(
      'div',
      { class: 'pdf-cover__head' },
      doc.artwork ? h('img', { class: 'pdf-cover__art', attrs: { src: doc.artwork, alt: '' } }) : null,
      h(
        'div',
        { class: 'pdf-cover__text' },
        h('p', { class: 'pdf-eyebrow', text: doc.kind }),
        h('h1', { class: 'pdf-cover__title', text: doc.title || 'Ohne Titel' }),
        doc.subtitle ? h('p', { class: 'pdf-cover__sub', text: doc.subtitle }) : null,
        h('p', { class: 'pdf-cover__facts', text: `${count} ${count === 1 ? 'Song' : 'Songs'} · ${contentLabel(opts.content)} · Stand ${dateText()}` }),
      ),
    ),
    opts.notes ? notesBlock(doc.notes ?? '') : null,
    h('h2', { class: 'pdf-section', text: 'Inhalt' }),
    toc,
    h('p', { class: 'pdf-foot', text: 'Lead Sheets · Schnittstelle Rot' }),
  );
}

export function contentLabel(content: PdfContent): string {
  return content === 'sheet' ? 'Lead Sheets' : content === 'lyrics' ? 'Songtexte' : 'Lead Sheets und Songtexte';
}

/* ---------------------------------------------------------- Bauen */

/** Baut das Dokument (ohne es einzuhaengen). Einzeln testbar. */
export function buildPdf(doc: PdfDocument, opts: PdfOptions): HTMLElement {
  const root = h('div', { class: 'print-root', attrs: { 'aria-hidden': 'true' } });
  const many = doc.songs.length > 1;
  if (many) root.append(coverPage(doc, opts));
  doc.songs.forEach((song, i) => {
    const running = many ? `${doc.title || doc.kind} · ${i + 1} / ${doc.songs.length}` : null;
    root.append(songPage(song, opts, running));
  });
  return root;
}

let active: HTMLElement | null = null;

function cleanup(): void {
  active?.remove();
  active = null;
  document.documentElement.classList.remove('is-printing');
}

const frame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

/**
 * Baut das Dokument, wartet auf Schriften und Bilder und oeffnet das
 * Druckfenster. Danach wird alles wieder entfernt.
 */
export async function printPdf(doc: PdfDocument, opts: PdfOptions): Promise<void> {
  cleanup();
  const root = buildPdf(doc, opts);
  active = root;
  document.body.append(root);

  await document.fonts?.ready;
  await Promise.all(
    [...root.querySelectorAll('img')].map((img) => img.decode().catch(() => undefined)),
  );
  await frame();
  root.querySelectorAll<HTMLElement>('.pdf-sheet__body').forEach((body) => fitGridBars(body));
  await frame();

  // Der Dokumenttitel wird zum Dateinamen des PDFs.
  const before = document.title;
  document.title = doc.title || 'Lead Sheet';
  document.documentElement.classList.add('is-printing');
  window.addEventListener(
    'afterprint',
    () => {
      document.title = before;
      cleanup();
    },
    { once: true },
  );
  window.print();
}
